import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    Alert,
    useTheme,
    useMediaQuery,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { 
    Visibility, 
    VisibilityOff,
    Email,
    Lock,
    CheckCircle
} from '@mui/icons-material';
import { authService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

interface ResetPasswordFormProps {
    open: boolean;
    onClose: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ open, onClose }) => {
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [resetCode, setResetCode] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { language } = useLanguage();

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const steps = [
        t('Solicitar Reset', 'Request Reset'),
        t('Ingresar Código', 'Enter Code'),
        t('Nueva Contraseña', 'New Password')
    ];

    const requestResetSchema = yup.object({
        email: yup.string()
            .email(t('Email inválido', 'Invalid email'))
            .required(t('El email es requerido', 'Email is required')),
    });

    const codeSchema = yup.object({
        reset_code: yup.string()
            .length(6, t('El código debe tener 6 dígitos', 'Code must be 6 digits'))
            .required(t('El código es requerido', 'Code is required')),
    });

    const passwordSchema = yup.object({
        new_password: yup.string()
            .min(8, t('La nueva contraseña debe tener al menos 8 caracteres', 'New password must be at least 8 characters'))
            .required(t('La nueva contraseña es requerida', 'New password is required')),
        confirm_password: yup.string()
            .oneOf([yup.ref('new_password')], t('Las contraseñas no coinciden', 'Passwords do not match'))
            .required(t('Confirma tu nueva contraseña', 'Confirm your new password')),
    });

    const requestResetFormik = useFormik({
        initialValues: { email: '' },
        validationSchema: requestResetSchema,
        onSubmit: async (values) => {
            try {
                setError('');
                setSuccess('');
                
                await authService.requestPasswordReset(values.email);
                setEmail(values.email);
                setSuccess(t('Código de restablecimiento enviado a tu email', 'Reset code sent to your email'));
                setCurrentStep(1);
                
            } catch (error: any) {
                console.error('Error al solicitar reset:', error);
                let errorMessage = t('Error al solicitar el restablecimiento', 'Error requesting reset');
                
                if (error.response?.status === 422) {
                    errorMessage = 'Datos inválidos. Verifica el email ingresado.';
                } else if (error.response?.status === 400) {
                    errorMessage = 'Solicitud inválida. Verifica los datos.';
                } else if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setError(errorMessage);
            }
        },
    });

    const codeFormik = useFormik({
        initialValues: { reset_code: '' },
        validationSchema: codeSchema,
        onSubmit: async (values) => {
            try {
                setError('');
                setResetCode(values.reset_code);
                setCurrentStep(2);
                
            } catch (error: any) {
                console.error('Error al validar código:', error);
                setError(t('Error al validar el código', 'Error validating code'));
            }
        },
    });

    const passwordFormik = useFormik({
        initialValues: {
            new_password: '',
            confirm_password: '',
        },
        validationSchema: passwordSchema,
        onSubmit: async (values) => {
            try {
                setError('');
                
                await authService.confirmPasswordReset({
                    email: email,
                    reset_code: resetCode,
                    new_password: values.new_password
                });
                
                setSuccess(t('Contraseña restablecida exitosamente', 'Password reset successfully'));
                
                // Cerrar el diálogo después de 2 segundos
                setTimeout(() => {
                    handleClose();
                }, 2000);
                
            } catch (error: any) {
                console.error('Error al confirmar reset:', error);
                let errorMessage = t('Error al restablecer la contraseña', 'Error resetting password');
                
                if (error.response?.status === 422) {
                    errorMessage = 'Datos inválidos. Verifica la información ingresada.';
                } else if (error.response?.status === 400) {
                    errorMessage = 'Código inválido o expirado.';
                } else if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setError(errorMessage);
            }
        },
    });

    const handleClose = () => {
        setCurrentStep(0);
        setError('');
        setSuccess('');
        setResetCode('');
        setEmail('');
        requestResetFormik.resetForm();
        codeFormik.resetForm();
        passwordFormik.resetForm();
        onClose();
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Box component="form" onSubmit={requestResetFormik.handleSubmit} sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="email"
                            label={t('Correo Electrónico', 'Email')}
                            type="email"
                            value={requestResetFormik.values.email}
                            onChange={requestResetFormik.handleChange}
                            error={requestResetFormik.touched.email && Boolean(requestResetFormik.errors.email)}
                            helperText={requestResetFormik.touched.email && requestResetFormik.errors.email}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: '#4ECDC4'
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '15px',
                                    '& fieldset': {
                                        borderColor: '#4ECDC4',
                                        borderWidth: '2px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#45B7D1'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#FF6B6B'
                                    }
                                }
                            }}
                        />
                        
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={requestResetFormik.isSubmitting}
                            sx={{
                                mt: 3,
                                borderRadius: '20px',
                                backgroundColor: '#FF6B6B',
                                fontWeight: 'bold',
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: '#FF5252',
                                    transform: 'scale(1.02)'
                                }
                            }}
                        >
                            {requestResetFormik.isSubmitting 
                                ? t('Enviando...', 'Sending...') 
                                : t('Enviar Código', 'Send Code')
                            }
                        </Button>
                    </Box>
                );
                
            case 1:
                return (
                    <Box component="form" onSubmit={codeFormik.handleSubmit} sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: '#666' }}>
                            {t('Ingresa el código de 6 dígitos enviado a', 'Enter the 6-digit code sent to')} <strong>{email}</strong>
                        </Typography>
                        
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="reset_code"
                            label={t('Código de Restablecimiento', 'Reset Code')}
                            value={codeFormik.values.reset_code}
                            onChange={codeFormik.handleChange}
                            error={codeFormik.touched.reset_code && Boolean(codeFormik.errors.reset_code)}
                            helperText={codeFormik.touched.reset_code && codeFormik.errors.reset_code}
                            inputProps={{ maxLength: 6 }}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: '#4ECDC4'
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '15px',
                                    '& fieldset': {
                                        borderColor: '#4ECDC4',
                                        borderWidth: '2px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#45B7D1'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#FF6B6B'
                                    }
                                }
                            }}
                        />
                        
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={codeFormik.isSubmitting}
                            sx={{
                                mt: 3,
                                borderRadius: '20px',
                                backgroundColor: '#FF6B6B',
                                fontWeight: 'bold',
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: '#FF5252',
                                    transform: 'scale(1.02)'
                                }
                            }}
                        >
                            {t('Verificar Código', 'Verify Code')}
                        </Button>
                        
                        <Button
                            fullWidth
                            variant="text"
                            onClick={() => setCurrentStep(0)}
                            sx={{
                                mt: 1,
                                color: '#4ECDC4',
                                '&:hover': {
                                    backgroundColor: 'rgba(78, 205, 196, 0.1)'
                                }
                            }}
                        >
                            {t('Cambiar Email', 'Change Email')}
                        </Button>
                    </Box>
                );
                
            case 2:
                return (
                    <Box component="form" onSubmit={passwordFormik.handleSubmit} sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="new_password"
                            label={t('Nueva Contraseña', 'New Password')}
                            type={showNewPassword ? "text" : "password"}
                            value={passwordFormik.values.new_password}
                            onChange={passwordFormik.handleChange}
                            error={passwordFormik.touched.new_password && Boolean(passwordFormik.errors.new_password)}
                            helperText={passwordFormik.touched.new_password && passwordFormik.errors.new_password}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: '#4ECDC4'
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '15px',
                                    '& fieldset': {
                                        borderColor: '#4ECDC4',
                                        borderWidth: '2px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#45B7D1'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#FF6B6B'
                                    }
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirm_password"
                            label={t('Confirmar Nueva Contraseña', 'Confirm New Password')}
                            type={showConfirmPassword ? "text" : "password"}
                            value={passwordFormik.values.confirm_password}
                            onChange={passwordFormik.handleChange}
                            error={passwordFormik.touched.confirm_password && Boolean(passwordFormik.errors.confirm_password)}
                            helperText={passwordFormik.touched.confirm_password && passwordFormik.errors.confirm_password}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: '#4ECDC4'
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '15px',
                                    '& fieldset': {
                                        borderColor: '#4ECDC4',
                                        borderWidth: '2px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#45B7D1'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#FF6B6B'
                                    }
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={passwordFormik.isSubmitting}
                            sx={{
                                mt: 3,
                                borderRadius: '20px',
                                backgroundColor: '#FF6B6B',
                                fontWeight: 'bold',
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: '#FF5252',
                                    transform: 'scale(1.02)'
                                }
                            }}
                        >
                            {passwordFormik.isSubmitting 
                                ? t('Restableciendo...', 'Resetting...') 
                                : t('Restablecer Contraseña', 'Reset Password')
                            }
                        </Button>
                    </Box>
                );
                
            default:
                return null;
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    border: '4px solid white',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                }
            }}
        >
            <DialogTitle sx={{ 
                textAlign: 'center', 
                color: '#FF6B6B',
                fontFamily: '"Dancing Script", cursive',
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 'bold',
                pb: 1
            }}>
                🔑 {t('Restablecer Contraseña', 'Reset Password')}
            </DialogTitle>
            
            <DialogContent>
                <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 2, 
                            borderRadius: '15px',
                            border: '2px solid #FF6B6B'
                        }}
                    >
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert 
                        severity="success" 
                        sx={{ 
                            mb: 2, 
                            borderRadius: '15px',
                            border: '2px solid #4CAF50'
                        }}
                    >
                        {success}
                    </Alert>
                )}
                
                {renderStepContent()}
            </DialogContent>
            
            <DialogActions sx={{ 
                justifyContent: 'center', 
                pb: 3, 
                px: 3
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '20px',
                        border: '2px solid #4ECDC4',
                        color: '#4ECDC4',
                        fontWeight: 'bold',
                        px: 3,
                        py: 1.5,
                        '&:hover': {
                            backgroundColor: 'rgba(78, 205, 196, 0.1)',
                            borderColor: '#45B7D1'
                        }
                    }}
                >
                    {t('Cancelar', 'Cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResetPasswordForm;
